import * as ColumnOptionsUtils from "components/column-options/utils";
import { ExpandedColumn, Filter } from "../types/columns";

type IsListed = (column: ExpandedColumn) => boolean;

const listedByDefault: IsListed = () => true;

/**
 * Shows or hides columns in the column options draft.
 * A column that gets shown moves right after the last shown column, so it appears last in the
 * "Selected" list; a column that gets hidden loses its filter, so it never filters unseen.
 * Fixed columns are left untouched.
 */
export const setColumnsVisibility = (
  columns: ExpandedColumn[],
  keys: string[],
  visible: boolean,
  isListed: IsListed = listedByDefault
): ExpandedColumn[] => {
  const changing = columns.filter(
    (c) => keys.includes(c.key) && !c.isFixed && !!c.isVisible !== visible
  );
  if (changing.length === 0) return columns;
  const changingKeys = changing.map((c) => c.key);

  if (!visible) {
    return columns.map((c) =>
      changingKeys.includes(c.key)
        ? {
            ...c,
            isVisible: false,
            filter: c.filter ? ColumnOptionsUtils.clearFilter(c.filter) : c.filter,
          }
        : c
    );
  }

  const rest = columns.filter((c) => !changingKeys.includes(c.key));
  let insertAt = 0;
  rest.forEach((c, index) => {
    if (c.isVisible && isListed(c)) insertAt = index + 1;
  });
  const shown = changing.map((c) => ({ ...c, isVisible: true }));
  return [...rest.slice(0, insertAt), ...shown, ...rest.slice(insertAt)];
};

const normalizeFilter = (filter: Filter) => {
  switch (filter.type) {
    case "taxonomy":
      return { type: filter.type, value: [...filter.value].sort() };
    case "date":
      return {
        type: filter.type,
        operator: filter.operator,
        date1: filter.date1 ? new Date(filter.date1).toDateString() : null,
        date2:
          filter.operator === "isBetween" && filter.date2
            ? new Date(filter.date2).toDateString()
            : null,
      };
    case "number":
      return {
        type: filter.type,
        operator: filter.operator,
        number1: Number(filter.number1),
        number2: filter.operator === "between" ? Number(filter.number2) : null,
        includeNumber1: filter.operator !== "eq" && !!filter.includeNumber1,
        includeNumber2: filter.operator === "between" && !!filter.includeNumber2,
      };
    default:
      return { type: filter.type, value: filter.value };
  }
};

/** What the column options panel can change: shown columns in order, and active filters. */
const columnsSignature = (columns: ExpandedColumn[], isListed: IsListed) =>
  JSON.stringify({
    shown: columns.filter((c) => c.isVisible && isListed(c)).map((c) => c.key),
    filters: columns
      .filter((c) => ColumnOptionsUtils.isColumnFiltered(c.filter))
      .map((c) => [c.key, normalizeFilter(c.filter!)])
      .sort(([a], [b]) => String(a).localeCompare(String(b))),
  });

/** True when both column lists show the same columns in the same order with the same filters. */
export const areColumnsEquivalent = (
  a: ExpandedColumn[],
  b: ExpandedColumn[],
  isListed: IsListed = listedByDefault
) => columnsSignature(a, isListed) === columnsSignature(b, isListed);
